import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Camera, Upload, X, Spinner } from '@phosphor-icons/react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const ImageUploader = ({ 
  currentImage, 
  onImageUploaded, 
  aspectRatio = 'square', // 'square', 'landscape', 'portrait'
  placeholder = 'Image Upload करा',
  className = ''
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage || null);
  const fileInputRef = useRef(null);

  const aspectClasses = {
    square: 'aspect-square',
    landscape: 'aspect-video',
    portrait: 'aspect-[3/4]'
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('फक्त JPEG, PNG, WebP, GIF images allowed आहेत');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image 5MB पेक्षा कमी असावी');
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload to server
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/upload/image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      const imageUrl = process.env.REACT_APP_BACKEND_URL + response.data.url;
      setPreview(imageUrl);
      onImageUploaded(imageUrl);
      toast.success('Image upload झाली! ✅');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Image upload failed. पुन्हा try करा.');
      setPreview(currentImage);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    onImageUploaded('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={`relative ${className}`}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
      />

      {preview ? (
        <div className={`relative ${aspectClasses[aspectRatio]} rounded-xl overflow-hidden border-2 border-dashed border-gray-200 bg-gray-50`}>
          <img
            src={preview}
            alt="Preview"
            className="w-full h-full object-cover"
          />
          
          {/* Overlay buttons */}
          <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <Button
              type="button"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-white text-gray-800 hover:bg-gray-100"
            >
              <Camera className="h-4 w-4 mr-1" />
              बदला
            </Button>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              onClick={handleRemove}
              disabled={uploading}
            >
              <X className="h-4 w-4 mr-1" />
              काढा
            </Button>
          </div>

          {/* Loading overlay */}
          {uploading && (
            <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
              <div className="text-center">
                <Spinner className="h-8 w-8 text-[#FF5A36] animate-spin mx-auto" />
                <p className="text-sm text-gray-600 mt-2">Uploading...</p>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div
          onClick={() => !uploading && fileInputRef.current?.click()}
          className={`${aspectClasses[aspectRatio]} rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-[#FF5A36] transition-all cursor-pointer flex flex-col items-center justify-center`}
        >
          {uploading ? (
            <>
              <Spinner className="h-10 w-10 text-[#FF5A36] animate-spin" />
              <p className="text-sm text-gray-500 mt-2">Uploading...</p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-pink-100 rounded-full flex items-center justify-center mb-3">
                <Upload className="h-8 w-8 text-[#FF5A36]" />
              </div>
              <p className="text-sm font-medium text-gray-700">{placeholder}</p>
              <p className="text-xs text-gray-500 mt-1">JPEG, PNG, WebP (Max 5MB)</p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
